import React from 'react'
import BaseSelect from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { InputLabel, FormControl, FormHelperText } from '../../Atoms'
import { makeStyles } from '@material-ui/styles'
import PropTypes from 'prop-types'
import clsx from 'clsx'

const useStyles = makeStyles(theme => ({
  root: {
    margin: 8,
    minWidth: 120
  }
}))

const propTypes = {
  /** Use the naming standards. */
  id: PropTypes.string.isRequired,
  /**
   * If `true`, the width of the popover will automatically be set according to the items inside the menu,
   * otherwise it will be at least the width of the select input.
   */
  autoWidth: PropTypes.bool,
  /** The default element value. Use when the component is not controlled. */
  defaultValue: PropTypes.any,
  /** If true, the select will be disabled. */
  disabled: PropTypes.bool,
  /** If true, the label should be displayed in an error state. */
  error: PropTypes.bool,
  /** The text to be used in the bottom of select*/
  helperText: PropTypes.string,
  /**
   * Array of options.
   *
   * `[{ label: 'The Shawshank Redemption', value: 1994 },`
   * `{ label: 'The Godfather', value: 1972 },`
   * `{ label: 'The Godfather: Part II', value: 1974 },`
   * `{ label: 'The Dark Knight', value: 2008 }]`
   *
   */
  options: PropTypes.array,
  /** If true, the label will indicate that the input is required. */
  required: PropTypes.bool,
  /** The text to be used in an enclosing label element. */
  label: PropTypes.string,
  /**
   * If `true`, the component will be using a native `select` element.
   *
   * As the user experience can be improved on mobile using the native select of the platform.
   */
  native: PropTypes.bool,
  /**
   * Callback function fired when a menu item is selected.
   *
   * Signature:
   * `function(event: object, child?: object) => void`
   * event: The event source of the callback.
   * You can pull out the new value by accessing
   * `event.target.value (any)`.
   * child: The react element that was selected when native is false (default).
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when the component requests to be closed. Use in controlled mode (see open).
   *
   * Signature:
   * `function(event: object) => void`
   * event: The event source of the callback.
   */
  onClose: PropTypes.func,
  /**
   * Callback fired when the component requests to be opened. Use in controlled mode (see open).
   *
   * Signature:
   * `function(event: object) => void`
   * event: The event source of the callback.
   */
  onOpen: PropTypes.func,
  /**
   * The input value. Providing an empty string will select no options.
   * This prop is required when the `native` prop is `false` (default). Set to an empty string `''` if you don't want any of the available options to be selected.
   * If the value is an object it must have reference equality with the option in order to be selected.
   * If the value is not an object, the string representation must match with the string representation of the option in order to be selected.
   */
  value: PropTypes.any,
  /** The size of the checkbox. small is equivalent to the dense checkbox styling. */
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled'])
}

const defaultProps = {
  autoWidth: false,
  variant: 'standard',
  options: [],
  native: false,
  error: false
}

/**
 *
 * ### Select components are used for collecting user provided information from a list of options.
 *
 * The `Select`is based on https://material-ui.com/components/selects/ we only use properties that has been define by our products.
 */

const Select = props => {
  const {
    options,
    label,
    id,
    helperText,
    autoWidth,
    defaultValue,
    disabled,
    native,
    onChange,
    onClose,
    onOpen,
    variant,
    value,
    className,
    error,
    required
  } = props

  const classes = useStyles()
  return (
    <FormControl
      data-cid='Select-root'
      className={clsx(classes.root, className)}
      error={error}
      required={required}
      disabled={disabled}
      variant={variant}
    >
      <InputLabel data-cid='Select-label' id={id + '_label'}>
        {label}
      </InputLabel>
      <BaseSelect
        data-cid='Select-input'
        id={id}
        autoWidth={autoWidth}
        defaultValue={defaultValue}
        disabled={disabled}
        native={native}
        onChange={onChange}
        onClose={onClose}
        onOpen={onOpen}
        variant={variant}
        value={value}
      >
        {options.map(item =>
          native ? (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ) : (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          )
        )}
      </BaseSelect>
      <FormHelperText data-cid='Select-helper'>{helperText}</FormHelperText>
    </FormControl>
  )
}

Select.propTypes = propTypes
Select.defaultProps = defaultProps

export default Select
